import { Logo } from "@/components/Logo";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ScanLine, Mail, Banknote, Search, ShieldCheck, Download, ShieldAlert, Globe, Droplet, UserCheck, CreditCard, Car, AlertCircle } from "lucide-react";

const FEATURES = [
  {
    icon: ScanLine,
    title: "Magic OCR",
    desc: "GPT-5.2 Vision reads faded thermal print with 99.4% accuracy — merchant, date, tax, total, every line item.",
  },
  {
    icon: Mail,
    title: "Multi-channel ingestion",
    desc: "Forward invoices to your personal @receiptsheets.com inbox or link Amazon, Walmart, DoorDash for auto-pull.",
  },
  {
    icon: Banknote,
    title: "Bank feed matching",
    desc: "Reconcile card transactions against uploaded receipts in real time — no more duplicate entries.",
  },
  {
    icon: Search,
    title: "Space-age search",
    desc: 'Fuzzy full-text search indexes every word. Type "laptop charger" and find the receipt in a keystroke.',
  },
  {
    icon: ShieldCheck,
    title: "Tax-deduction flags",
    desc: "Auto-categorize merchants and flag write-offs so year-end filing takes minutes, not weekends.",
  },
  {
    icon: Download,
    title: "IRS-compliant archive",
    desc: "One-tap CSV or PDF export. Every archive stored in certified high-resolution.",
  },
  {
    icon: ShieldAlert,
    title: "AI Audit-Shield & Discrepancy Alerts",
    desc: "Auto-detect billing mistakes and merchant overcharges before they cost you a cent.",
  },
  {
    icon: Globe,
    title: "Autonomous FX Multi-Currency Conversion",
    desc: "Scan local foreign currencies (€, ¥, £) to instantly view live, real-time USD balances.",
  },
  {
    icon: Droplet,
    title: "Subscription Leak Detector",
    desc: "Flags abandoned recurring charges before they hit your account, month after month.",
  },
  {
    icon: UserCheck,
    title: "Employee Approval Queue",
    desc: "Review team expenses and dispatch mock ACH payouts with one tap. Full audit log included.",
  },
  {
    icon: AlertCircle,
    title: "Policy Compliance Caps",
    desc: "Set per-category limits (meals > $50, etc.) and auto-flag every violation as it's uploaded.",
  },
  {
    icon: CreditCard,
    title: "Virtual Card Generator",
    desc: "Issue category-locked virtual cards with spend limits and instant freeze — perfect for team subscriptions.",
  },
  {
    icon: Car,
    title: "IRS Mileage Tracker",
    desc: "Log business trips and auto-calculate deductions at the 2026 rate ($0.70/mile). Ready for Schedule C.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const cta = () => {
    if (!user) navigate("/login");
    else if (user.subscription_status !== "active" && user.subscription_status !== "active_business") navigate("/paywall");
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen grid-bg">
      <header className="px-8 py-6 flex items-center justify-between max-w-[1400px] mx-auto">
        <Logo size="md" />
        <nav className="flex items-center gap-4">
          <button
            onClick={() => navigate("/login")}
            data-testid="landing-login-btn"
            className="text-slate-300 hover:text-white text-sm font-medium"
          >
            Sign in
          </button>
          <button
            onClick={cta}
            data-testid="landing-cta-header"
            className="h-10 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm"
          >
            Get started
          </button>
        </nav>
      </header>

      <section className="max-w-[1200px] mx-auto px-8 pt-16 pb-24 text-center fade-up">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs font-semibold uppercase tracking-widest mb-8">
          Premium receipt vault · Now live
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02] text-white max-w-4xl mx-auto">
          Every receipt.<br />
          <span className="text-emerald-400">Every deduction.</span> Zero effort.
        </h1>
        <p className="mt-8 text-lg text-slate-300 max-w-2xl mx-auto">
          Receipt Sheets is the premium-only vault where GPT-5.2 Vision handles the
          data entry, bank feeds reconcile themselves, and the IRS never wins.
        </p>
        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            onClick={cta}
            data-testid="landing-cta-hero"
            className="btn-neon h-14 px-8 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base flex items-center gap-2"
          >
            Start scanning — from $9.99/mo <ArrowRight size={18} />
          </button>
        </div>
        <div className="mt-6 text-xs font-mono uppercase tracking-widest text-slate-500">
          No credit card until you pick a plan · Cancel anytime
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-8 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {FEATURES.map((f) => (
            <div key={f.title} className="glass rounded-2xl p-7 card-hover">
              <f.icon size={22} className="text-emerald-400 mb-4" />
              <div className="text-lg font-semibold text-white tracking-tight">
                {f.title}
              </div>
              <div className="text-slate-400 text-sm mt-2 leading-relaxed">
                {f.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-8 pb-24 text-center">
        <div className="glass rounded-3xl p-12">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Ready to reclaim your write-offs?
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Pick Pro or Business, sign in with Google, and start scanning in under 30
            seconds.
          </p>
          <button
            onClick={cta}
            data-testid="landing-cta-footer"
            className="btn-neon mt-8 h-14 px-8 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-base inline-flex items-center gap-2"
          >
            Choose your plan <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-8 max-w-[1400px] mx-auto flex flex-wrap items-center justify-between gap-4">
        <Logo size="sm" />
        <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">
          © 2026 Receipt Sheets · SOC 2 · 256-bit AES · GDPR
        </div>
      </footer>
    </div>
  );
}
